// Canonical curriculum hierarchy: grouping, de-duplication, browsing.
import { t } from "../i18n.js";
import { escapeHTML } from "../lib/dom.js";
import { difficultyBandInfo, localizedExerciseCount, topicDisplayInfo } from "../lib/format.js";
import { state } from "../state.js";

export function normalizedCurriculumLabel(value) {
  return String(value || "")
    .toLocaleLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function learningEntryForExercise(exercise) {
  const code = String(exercise?.learning_topic_code || exercise?.category_code || "").trim().toUpperCase();
  const id = Number(exercise?.learning_topic_id || exercise?.subcategory_id || 0);
  if (code) {
    const byCode = state.learningHierarchy.find(item => item.learning_topic_code === code);
    if (byCode) return byCode;
  }
  if (id) {
    const byID = state.learningHierarchy.find(item => Number(item.learning_topic_id) === id);
    if (byID) return byID;
  }
  return null;
}

export function exerciseQuestionCount(exercise) {
  return Number(exercise?.question_count ?? exercise?.questions?.length ?? exercise?.total_questions ?? 0);
}

export function canonicalExerciseDescriptor(exercise) {
  const display = topicDisplayInfo(exercise?.topic);
  const learning = learningEntryForExercise(exercise || {});
  const learningTopicCode = String(learning?.learning_topic_code || exercise?.learning_topic_code || exercise?.category_code || display.key || "other").trim().toUpperCase();
  const activityTagCode = String(exercise?.topic_code || learningTopicCode).trim().toUpperCase();
  const band = exercise?.difficulty_band || difficultyBandInfo(exercise?.difficulty_level).code;
  const exerciseNumber = Math.max(1, Number(exercise?.exercise_number || 1));
  return {
    key: `${activityTagCode}|${band}|${exerciseNumber}`,
    visibleKey: `${learningTopicCode}|${band}`,
    topicCode: learningTopicCode,
    topicName: learning?.learning_topic_name || exercise?.category_name || display.title,
    topicSort: Number(learning?.learning_topic_sort || 999),
    activityTagCode,
    activityTagName: exercise?.topic_name || "",
    categoryCode: learningTopicCode,
    categoryName: learning?.learning_topic_name || exercise?.category_name || display.title,
    categorySort: Number(learning?.learning_topic_sort || 999),
    macroCode: learning?.area_code || exercise?.macro_category_code || `OTHER:${normalizedCurriculumLabel(display.macro || "Altro")}`,
    macroName: learning?.area_name || exercise?.macro_category_name || display.macro || (state.language === "it" ? "Altri argomenti" : "Other topics"),
    macroSort: Number(learning?.area_sort || 999),
    band,
    bandInfo: difficultyBandInfo(exercise?.difficulty_level),
    exerciseNumber
  };
}

export function dedupeCanonicalExerciseRecords(records, scopeKey = () => "") {
  const canonical = new Map();
  (records || []).forEach(record => {
    const descriptor = canonicalExerciseDescriptor(record);
    const key = `${scopeKey(record)}|${descriptor.key}`;
    const questionCount = exerciseQuestionCount(record);
    if (!canonical.has(key)) {
      canonical.set(key, {
        ...record,
        ...descriptor,
        canonical_key: descriptor.key,
        duplicate_count: 1,
        exercise_ids: record.exercise_id ? [record.exercise_id] : [],
        assignment_ids: record.assignment_id ? [record.assignment_id] : [],
        question_count: questionCount
      });
      return;
    }
    const current = canonical.get(key);
    current.duplicate_count += 1;
    if (record.exercise_id && !current.exercise_ids.includes(record.exercise_id)) current.exercise_ids.push(record.exercise_id);
    if (record.assignment_id && !current.assignment_ids.includes(record.assignment_id)) current.assignment_ids.push(record.assignment_id);
    current.assigned_students = Math.max(Number(current.assigned_students || 0), Number(record.assigned_students || 0));
    current.pending_students = Math.max(Number(current.pending_students || 0), Number(record.pending_students || 0));
    current.completed_students = Math.max(Number(current.completed_students || 0), Number(record.completed_students || 0));
    if (questionCount > Number(current.question_count || 0)) {
      const preserved = {
        duplicate_count: current.duplicate_count,
        exercise_ids: current.exercise_ids,
        assignment_ids: current.assignment_ids,
        assigned_students: current.assigned_students,
        pending_students: current.pending_students,
        completed_students: current.completed_students
      };
      Object.assign(current, record, descriptor, preserved, { canonical_key: descriptor.key, question_count: questionCount });
    }
  });
  return [...canonical.values()];
}

export function canonicalExerciseCount(container) {
  if (container?.exercises instanceof Map) return container.exercises.size;
  if (container?.topics instanceof Map) {
    return [...container.topics.values()].reduce((sum, topic) => sum + canonicalExerciseCount(topic), 0);
  }
  if (container?.subcategories instanceof Map) {
    return [...container.subcategories.values()].reduce((sum, subcategory) => sum + canonicalExerciseCount(subcategory), 0);
  }
  if (container?.macros instanceof Map) {
    return [...container.macros.values()].reduce((sum, macro) => sum + canonicalExerciseCount(macro), 0);
  }
  return 0;
}

export function canonicalExerciseValues(container) {
  if (container?.exercises instanceof Map) return [...container.exercises.values()];
  if (container?.topics instanceof Map) return [...container.topics.values()].flatMap(canonicalExerciseValues);
  if (container?.subcategories instanceof Map) return [...container.subcategories.values()].flatMap(canonicalExerciseValues);
  if (container?.macros instanceof Map) return [...container.macros.values()].flatMap(canonicalExerciseValues);
  return [];
}

export function groupTeacherExercises(exercises) {
  const classes = new Map();
  exercises.forEach(exercise => {
    const classKey = exercise.class_id || "unassigned";
    if (!classes.has(classKey)) {
      classes.set(classKey, {
        id: classKey,
        name: exercise.class_name || (state.language === "it" ? "Senza classe" : "Unassigned"),
        school: exercise.school_name || "",
        academicYear: exercise.academic_year_name || "",
        macros: new Map()
      });
    }
    const classroom = classes.get(classKey);
    const descriptor = canonicalExerciseDescriptor(exercise);
    const macroKey = descriptor.macroCode;
    const subcategoryKey = descriptor.topicCode;
    if (!classroom.macros.has(macroKey)) {
      classroom.macros.set(macroKey, {
        code: macroKey,
        name: descriptor.macroName,
        sort: descriptor.macroSort,
        subcategories: new Map()
      });
    }
    const macro = classroom.macros.get(macroKey);
    if (!macro.subcategories.has(subcategoryKey)) {
      macro.subcategories.set(subcategoryKey, {
        code: subcategoryKey,
        name: descriptor.topicName,
        sort: descriptor.topicSort,
        exercises: new Map()
      });
    }
    const subcategory = macro.subcategories.get(subcategoryKey);
    const band = descriptor.band;
    const exerciseNumber = descriptor.exerciseNumber;
    const groupKey = descriptor.key;
    if (!subcategory.exercises.has(groupKey)) {
      subcategory.exercises.set(groupKey, {
        ...exercise,
        difficulty_band: band,
        exercise_number: exerciseNumber,
        exercise_ids: [],
        duplicate_count: 0,
        question_count: 0,
        assigned_students: 0,
        pending_students: 0,
        completed_students: 0
      });
    }
    const canonical = subcategory.exercises.get(groupKey);
    if (exercise.exercise_id && !canonical.exercise_ids.includes(exercise.exercise_id)) canonical.exercise_ids.push(exercise.exercise_id);
    canonical.duplicate_count += 1;
    canonical.question_count = Math.max(canonical.question_count, exerciseQuestionCount(exercise));
    canonical.assigned_students = Math.max(canonical.assigned_students, Number(exercise.assigned_students || 0));
    canonical.pending_students = Math.max(canonical.pending_students, Number(exercise.pending_students || 0));
    canonical.completed_students = Math.max(canonical.completed_students, Number(exercise.completed_students || 0));
  });
  return [...classes.values()];
}

function renderTeacherTopicExercises(topicGroup) {
  const byBand = new Map();
  [...topicGroup.exercises.values()].forEach(exercise => {
    const band = difficultyBandInfo(exercise.difficulty_level);
    if (!byBand.has(band.code)) byBand.set(band.code, { band, exercises: [] });
    byBand.get(band.code).exercises.push(exercise);
  });
  return ["base", "medio", "avanzato", "esperto"].map(code => {
    const group = byBand.get(code);
    if (!group) return "";
    const exercises = group.exercises.sort((a, b) => Number(a.exercise_number || 1) - Number(b.exercise_number || 1));
    return `<section class="teacher-difficulty-section">
      <div class="teacher-difficulty-heading">
        <span class="teacher-difficulty-icon">${group.band.icon}</span>
        <div><strong>${escapeHTML(group.band.label)}</strong><small>${localizedExerciseCount(exercises.length)}</small></div>
      </div>
      <div class="exercise-list">
        ${exercises.map((exercise, index) => `
          <button class="exercise-card" data-open-teacher-exercise="${exercise.exercise_id}">
            <span class="exercise-symbol">${index + 1}</span>
            <span>
              <h3>${state.language === "it" ? "Attività" : "Activity"} ${index + 1}</h3>
              <p>${exercise.question_count} ${t("questions")} · ${exercise.assigned_students} ${state.language === "it" ? "assegnati" : "assigned"} · ${exercise.completed_students} ${state.language === "it" ? "completati" : "completed"}</p>
            </span>
            <span class="chevron">›</span>
          </button>`).join("")}
      </div>
    </section>`;
  }).join("");
}

export function renderTeacherExerciseHierarchy(classroom) {
  const macros = [...classroom.macros.values()].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
  return macros.map(macro => `
    <details class="teacher-hierarchy-node teacher-macro-node" open>
      <summary>
        <span class="teacher-node-symbol">📘</span>
        <span class="teacher-node-title"><small>${state.language === "it" ? "Area matematica" : "Math area"}</small><strong>${escapeHTML(macro.name)}</strong></span>
        <span class="topic-summary-meta">${localizedExerciseCount(canonicalExerciseCount(macro))}</span>
        <span class="chevron">⌄</span>
      </summary>
      <div class="teacher-hierarchy-children">
        ${[...macro.subcategories.values()].sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name)).map(subcategory => `
          <details class="teacher-hierarchy-node teacher-subcategory-node" open>
            <summary>
              <span class="teacher-node-symbol">▦</span>
              <span class="teacher-node-title"><small>${state.language === "it" ? "Argomento" : "Topic"}</small><strong>${escapeHTML(subcategory.name)}</strong></span>
              <span class="topic-summary-meta">${localizedExerciseCount(canonicalExerciseCount(subcategory))}</span>
              <span class="chevron">⌄</span>
            </summary>
            <div class="teacher-hierarchy-children">
              <div class="teacher-topic-content">${renderTeacherTopicExercises(subcategory)}</div>
            </div>
          </details>`).join("")}
      </div>
    </details>`).join("");
}

export function curriculumBrowser(exercises, mode, teacherGrades = new Set()) {
  const path = state.catalogNavigation[mode];
  const canonicalExercises = dedupeCanonicalExerciseRecords(exercises);
  const allowedTree = mode === "teacher"
    ? state.learningHierarchy.filter(item =>
        [...teacherGrades].some(grade => grade >= item.min_grade && grade <= item.max_grade)
      )
    : state.learningHierarchy;
  const selectedGrade = Number(path.grade || 0);
  const tree = selectedGrade
    ? allowedTree.filter(item => selectedGrade >= item.min_grade && selectedGrade <= item.max_grade)
    : allowedTree;
  const visibleExercises = selectedGrade
    ? canonicalExercises.filter(exercise => {
        const range = state.catalogGradeRanges[exercise.exercise_id];
        return range && selectedGrade >= range.min_grade && selectedGrade <= range.max_grade;
      })
    : canonicalExercises;
  const countFor = predicate => visibleExercises.filter(predicate).length;
  const schoolName = path.schoolLevel === "primary"
    ? (state.language === "it" ? "Primaria" : "Primary")
    : path.schoolLevel === "middle"
      ? (state.language === "it" ? "Medie" : "Middle school")
      : null;
  const displayGrade = selectedGrade ? (selectedGrade <= 5 ? selectedGrade : selectedGrade - 5) : null;
  const breadcrumb = [
    `<button class="link-button" data-catalog-home="${mode}">${state.language === "it" ? "Catalogo" : "Catalogue"}</button>`
  ];
  if (schoolName) breadcrumb.push(`<button class="link-button" data-catalog-school-level="${path.schoolLevel}" data-mode="${mode}">${schoolName}</button>`);
  if (selectedGrade) breadcrumb.push(`<button class="link-button" data-catalog-grade="${selectedGrade}" data-mode="${mode}">${state.language === "it" ? "Classe" : "Class"} ${displayGrade}ª</button>`);

  const selectedMacro = tree.find(item => item.area_code === path.macro);
  const selectedLearningTopic = tree.find(item => item.learning_topic_code === path.learningTopic);
  const difficultyBands = [
    { code: "base", name: state.language === "it" ? "Base" : "Basic", range: "1–2", symbol: "🌱" },
    { code: "medio", name: state.language === "it" ? "Medio" : "Intermediate", range: "3–5", symbol: "⭐" },
    { code: "avanzato", name: state.language === "it" ? "Avanzato" : "Advanced", range: "6–8", symbol: "🚀" },
    { code: "esperto", name: state.language === "it" ? "Esperto" : "Expert", range: "9–10", symbol: "🏆" }
  ];
  const selectedDifficulty = difficultyBands.find(item => item.code === path.difficulty);
  if (selectedMacro) breadcrumb.push(`<button class="link-button" data-catalog-macro="${selectedMacro.area_code}" data-mode="${mode}">${escapeHTML(selectedMacro.area_name)}</button>`);
  if (selectedLearningTopic) breadcrumb.push(`<button class="link-button" data-catalog-learning-topic="${selectedLearningTopic.learning_topic_code}" data-mode="${mode}">${escapeHTML(selectedLearningTopic.learning_topic_name)}</button>`);
  if (selectedDifficulty) breadcrumb.push(`<span>${escapeHTML(selectedDifficulty.name)}</span>`);

  let title = state.language === "it" ? "Catalogo di matematica" : "Mathematics catalogue";
  let help = state.language === "it" ? "Scegli l’ordine scolastico." : "Choose the school level.";
  let content = "";

  if (!path.schoolLevel) {
    const levels = [
      { code: "primary", name: state.language === "it" ? "Scuola primaria" : "Primary school", grades: [1,2,3,4,5], symbol: "🏫" },
      { code: "middle", name: state.language === "it" ? "Scuola secondaria di primo grado" : "Middle school", grades: [6,7,8], symbol: "🎓" }
    ].filter(level => mode !== "teacher" || level.grades.some(grade => teacherGrades.has(grade)));
    content = levels.map(level => `
      <button class="exercise-card" data-catalog-school-level="${level.code}" data-mode="${mode}">
        <span class="exercise-symbol">${level.symbol}</span>
        <span><h3>${escapeHTML(level.name)}</h3><p>${level.grades.filter(grade => mode !== "teacher" || teacherGrades.has(grade)).length} ${state.language === "it" ? "classi disponibili" : "available classes"}</p></span>
        <span class="chevron">›</span>
      </button>`).join("");
  } else if (!path.grade) {
    title = schoolName;
    help = state.language === "it" ? "Scegli la classe." : "Choose the class.";
    const grades = (path.schoolLevel === "primary" ? [1,2,3,4,5] : [6,7,8])
      .filter(grade => mode !== "teacher" || teacherGrades.has(grade));
    content = grades.map(grade => {
      const count = canonicalExercises.filter(exercise => {
        const range = state.catalogGradeRanges[exercise.exercise_id];
        return range && grade >= range.min_grade && grade <= range.max_grade;
      }).length;
      return `<button class="exercise-card" data-catalog-grade="${grade}" data-mode="${mode}">
      <span class="exercise-symbol">${grade <= 5 ? grade : grade - 5}</span>
        <span><h3>${state.language === "it" ? "Classe" : "Class"} ${grade <= 5 ? grade : grade - 5}ª</h3><p>${localizedExerciseCount(count)} ${state.language === "it" ? "compatibili" : "compatible"}</p></span>
        <span class="chevron">›</span>
      </button>`;
    }).join("");
  } else if (!path.macro) {
    title = `${schoolName} · ${state.language === "it" ? "Classe" : "Class"} ${displayGrade}ª`;
    help = state.language === "it" ? "Scegli l’area matematica." : "Choose the maths area.";
    const macros = [...new Map(tree.map(item => [item.area_code, {
      code: item.area_code, name: item.area_name, sort: item.area_sort
    }])).values()].sort((a,b) => a.sort - b.sort);
    content = macros.map(item => `<button class="exercise-card" data-catalog-macro="${item.code}" data-mode="${mode}">
      <span class="exercise-symbol">📘</span><span><h3>${escapeHTML(item.name)}</h3><p>${localizedExerciseCount(countFor(exercise => canonicalExerciseDescriptor(exercise).macroCode === item.code))}</p></span><span class="chevron">›</span>
    </button>`).join("");
  } else if (!path.learningTopic) {
    title = selectedMacro?.area_name || title;
    help = state.language === "it" ? "Scegli l’argomento." : "Choose the topic.";
    const items = [...new Map(tree.filter(item => item.area_code === path.macro).map(item => [item.learning_topic_code, {
      code:item.learning_topic_code, name:item.learning_topic_name, sort:item.learning_topic_sort
    }])).values()].sort((a,b) => a.sort-b.sort);
    content = items.map(item => `<button class="exercise-card" data-catalog-learning-topic="${item.code}" data-mode="${mode}">
      <span class="exercise-symbol">▦</span><span><h3>${escapeHTML(item.name)}</h3><p>${localizedExerciseCount(countFor(exercise => canonicalExerciseDescriptor(exercise).topicCode === item.code))}</p></span><span class="chevron">›</span>
    </button>`).join("");
  } else if (!path.difficulty) {
    title = selectedLearningTopic?.learning_topic_name || title;
    help = state.language === "it" ? "Scegli la difficoltà." : "Choose a difficulty.";
    const topicExercises = visibleExercises.filter(exercise => canonicalExerciseDescriptor(exercise).topicCode === path.learningTopic);
    content = difficultyBands.map(band => `<button class="exercise-card" data-catalog-difficulty="${band.code}" data-mode="${mode}">
      <span class="exercise-symbol">${band.symbol}</span><span><h3>${band.name}</h3><p>${localizedExerciseCount(topicExercises.filter(exercise => exercise.difficulty_band === band.code).length)}</p></span><span class="chevron">›</span>
    </button>`).join("");
  } else {
    title = `${selectedLearningTopic?.learning_topic_name || ""} · ${selectedDifficulty?.name || ""}`;
    help = state.language === "it" ? "Esercizi compatibili con la classe selezionata." : "Exercises compatible with the selected class.";
    const topicExercises = visibleExercises.filter(exercise =>
      canonicalExerciseDescriptor(exercise).topicCode === path.learningTopic && exercise.difficulty_band === path.difficulty
    ).sort((a,b) => Number(a.exercise_number||1)-Number(b.exercise_number||1));
    content = topicExercises.map((exercise, index) => `<div class="exercise-card">
      <span class="exercise-symbol">${index + 1}</span>
      <span><h3>${state.language === "it" ? "Attività" : "Activity"} ${index + 1}</h3><p>${state.language === "it" ? "Scelta multipla" : "Multiple choice"} · ${exercise.question_count ?? exercise.questions?.length ?? 0} ${t("questions")}</p></span>
      ${mode === "teacher"
        ? `<button class="primary" data-assign-catalog="${exercise.exercise_id}">${state.language === "it" ? "Assegna" : "Assign"}</button>`
        : `<button class="${exercise.available_to_teachers ? "secondary" : "primary"}" data-toggle-catalog="${exercise.exercise_id}" data-available="${exercise.available_to_teachers}">${exercise.available_to_teachers ? (state.language === "it" ? "Nascondi" : "Hide") : (state.language === "it" ? "Pubblica" : "Publish")}</button>`}
    </div>`).join("");
  }

  return `<div class="catalog-breadcrumb">${breadcrumb.join('<span class="chevron">›</span>')}</div>
    <div class="library-heading"><div><span class="eyebrow">${state.language === "it" ? "Catalogo didattico" : "Learning catalogue"}</span><h2>${escapeHTML(title || "")}</h2><p>${help}</p></div>
    ${(path.schoolLevel || path.grade || path.macro || path.learningTopic || path.difficulty) ? `<button class="secondary" data-catalog-back="${mode}">${state.language === "it" ? "Indietro" : "Back"}</button>` : ""}</div>
    <div class="exercise-list">${content}</div>`;
}

export function bindCurriculumBrowser(mode, renderView) {
  const path = state.catalogNavigation[mode];
  document.querySelectorAll(`[data-mode="${mode}"][data-catalog-school-level]`).forEach(button => button.addEventListener("click", () => {
    state.catalogNavigation[mode] = { schoolLevel:button.dataset.catalogSchoolLevel, grade:null, macro:null, learningTopic:null, difficulty:null };
    renderView();
  }));
  document.querySelectorAll(`[data-mode="${mode}"][data-catalog-grade]`).forEach(button => button.addEventListener("click", () => {
    path.grade=Number(button.dataset.catalogGrade); path.macro=null; path.learningTopic=null; path.difficulty=null; renderView();
  }));
  document.querySelectorAll(`[data-mode="${mode}"][data-catalog-macro]`).forEach(button => button.addEventListener("click", () => {
    path.macro=button.dataset.catalogMacro; path.learningTopic=null; path.difficulty=null; renderView();
  }));
  document.querySelectorAll(`[data-mode="${mode}"][data-catalog-learning-topic]`).forEach(button => button.addEventListener("click", () => {
    path.learningTopic=button.dataset.catalogLearningTopic; path.difficulty=null; renderView();
  }));
  document.querySelectorAll(`[data-mode="${mode}"][data-catalog-difficulty]`).forEach(button => button.addEventListener("click", () => {
    path.difficulty=button.dataset.catalogDifficulty; renderView();
  }));
  document.querySelectorAll(`[data-catalog-home="${mode}"]`).forEach(button => button.addEventListener("click", () => {
    state.catalogNavigation[mode]={schoolLevel:null,grade:null,macro:null,learningTopic:null,difficulty:null}; renderView();
  }));
  document.querySelectorAll(`[data-catalog-back="${mode}"]`).forEach(button => button.addEventListener("click", () => {
    if(path.difficulty) path.difficulty=null; else if(path.learningTopic) path.learningTopic=null;
    else if(path.macro) path.macro=null; else if(path.grade) path.grade=null; else path.schoolLevel=null;
    renderView();
  }));
}
